import { Field, useField } from "react-final-form";
import { Alert, FormText } from "reactstrap";
import { SelectInput } from "../components/inputs/final_form/SelectInput";
import { Fieldset } from "../components/inputs/final_form/Fieldset";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormRow } from "../components/forms/FormRow";
import { TextInput } from "../components/inputs/final_form/TextInput";
import { numericInRange, required } from "../util/validators";
import { LoadingButton } from "../components/LoadingButton";
import { isEmpty } from "lodash";
import { trackEvent } from "../util/track";
import { StatusTheCaseFields } from "../submit_right_direction/components/StatusTheCaseFields";

const REQUIRED_STATUS_FIELDS = {
  approved: ["authorizationNumber", "authorizationDate"],
  denied: [],
  documented_clean_claim: ["submissionReferenceNumber"],
  medicalreview: [],
  partially_approved: ["authorizationNumber", "authorizationDate", "approvedCptCodes"],
  submitted: [],
  rejected: [],
};

const DISPLAY_STATUS_FIELDS = {
  approved: ["authorizationNumber", "authorizationDate", "authorizationEffectiveDate", "authorizationExpirationDate"],
  denied: ["denialReasonCode", "submissionReferenceNumber"],
  documented_clean_claim: ["submissionReferenceNumber"],
  medicalreview: ["submissionReferenceNumber", "expectedCloseDate"],
  partially_approved: [
    "authorizationNumber",
    "authorizationDate",
    "authorizationEffectiveDate",
    "authorizationExpirationDate",
    "approvedCptCodes",
    "deniedCptCodes",
    "denialReasonCode",
  ],
  code_specific_result: ["approvedCptCodes", "deniedCptCodes", "documentedCleanClaimCptCodes"],
  submitted: [],
  rejected: [],
};

export const SubmitRightPaperResponseForm = ({
  paperResponses,
  options,
  handleSubmit,
  form,
  values,
  confirmed,
  verifiedCptCodeOptions,
  getPaperResponseObj,
  denialReasonCodeOptions,
  paSubstatuses,
  paStatuses,
  request,
  setConfirmedFunction,
  caseId,
  setShowFax,
  ...rest
}) => {
  const [loading, setLoading] = useState(false);

  const paperResponse = useField("paperSubmissionResponse.message", {
    subscription: { value: true },
  })?.input?.value;

  const approvedCptCodesValue = useField("paperSubmissionResponse.approvedCptCodes", { subscription: { value: true } })?.input?.value;
  const deniedCptCodesValue = useField("paperSubmissionResponse.deniedCptCodes", { subscription: { value: true } })?.input?.value;
  const cleanClaimCptCodesValue = useField("paperSubmissionResponse.documentedCleanClaimCptCodes", { subscription: { value: true } })?.input
    ?.value;

  const setConfirmed = useCallback(
    (value) => {
      setConfirmedFunction(value);
    },
    [setConfirmedFunction]
  );

  // Upon choosing a paper response, we clear out the confirmation.
  // We don't want this hook to fire any other time; the linter is wrong here.
  useEffect(() => {
    if (confirmed) {
      setConfirmed(false);
      setShowFax(false);
    }
  }, [paperResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  const responseObj = !isEmpty(paperResponse) && getPaperResponseObj(paperResponse);
  const { status } = responseObj;

  const parseStatusAndSubstatus = (responseObj) => {
    const parsedStatus = responseObj.status;
    const parsedSubstatus = responseObj.substatus;

    form.change("paperSubmissionResponse.status", parsedStatus);
    form.change("paperSubmissionResponse.substatus", parsedSubstatus);
  };

  const isCodeSpecificResult = useMemo(() => status === "code_specific_result", [status]);

  const handleConfirm = () => {
    setLoading(true);

    if (responseObj?.message !== undefined) {
      form.change("paperSubmissionResponse.paperResponseId", responseObj.id);
      form.change("paperSubmissionResponse.submitRightDirection", responseObj.submit_right_direction);
      form.change("paperSubmissionResponse.cannedComment", responseObj.canned_comment);

      if (responseObj.submit_right_direction == "status_the_case") {
        parseStatusAndSubstatus(responseObj);

        if (responseObj?.denial_reason_code) {
          form.change("paperSubmissionResponse.denialReasonCode", responseObj.denial_reason_code);
        }
      } else if (responseObj.submit_right_direction == "task_out_for_n_number_of_days_to_try_again") {
        form.change("paperSubmissionResponse.taskName", responseObj.task_name);
        form.change("paperSubmissionResponse.taskDuration", responseObj.task_duration);

        trackEvent("task_out_case_sr2_paper_response", caseId, {
          step: "task_out_case_sr2_paper_response",
        });
      } else if (responseObj.submit_right_direction == "resubmit_to_portal") {
        trackEvent("resubmit_to_portal_sr2_paper_response", caseId, {
          step: "resubmit_to_portal_sr2_paper_response",
        });
      }
    }
    trackEvent("paper_submission_confirm_button", caseId, {
      step: "paper_submission_confirm_button",
      data: {
        direction: responseObj?.submit_right_direction,
        comment: responseObj?.canned_comment,
      },
    });

    setConfirmed(true);
    setLoading(false);
  };

  return (
    <>
      <Fieldset legend="Paper Submission Results">
        <FormRow>
          <Field
            label="Paper Submission Response"
            name="paperSubmissionResponse.message"
            component={SelectInput}
            options={options}
            validate={required}
          >
            <FormText>Change it to restart...</FormText>
          </Field>
        </FormRow>

        {confirmed && responseObj?.submit_right_direction === "task_out_for_n_number_of_days_to_try_again" && (
          <>
            <Field component={TextInput} label="Task Name" name="paperSubmissionResponse.taskName" disabled={true} />
            <Field
              component={TextInput}
              label="Task Duration(Days)"
              name="paperSubmissionResponse.taskDuration"
              validate={numericInRange(0, 90)}
              min="0"
              max="90"
              disabled={true}
            />
          </>
        )}

        {confirmed && responseObj?.submit_right_direction === "status_the_case" && (
          <>
            <StatusTheCaseFields
              verifiedCptCodeOptions={verifiedCptCodeOptions}
              isCodeSpecificResult={isCodeSpecificResult}
              status={status}
              displayStatusMap={DISPLAY_STATUS_FIELDS}
              requiredStatusMap={REQUIRED_STATUS_FIELDS}
              formLink={"paperSubmissionResponse"}
              request={request}
              denialReasonCodeOptions={denialReasonCodeOptions}
              messageObj={responseObj}
              approvedCptCodes={approvedCptCodesValue}
              deniedCptCodes={deniedCptCodesValue}
              cleanClaimCptCodes={cleanClaimCptCodesValue}
            />
          </>
        )}

        {confirmed && responseObj && (
          <>
            <Field component={TextInput} label="Canned Comment" name="paperSubmissionResponse.cannedComment" />
          </>
        )}

        {confirmed && responseObj?.submit_right_direction === "re_send_fax" && <Alert color="info">A Fax Form will open.</Alert>}

        {confirmed && responseObj?.submit_right_direction === "resubmit_to_portal" && (
          <Alert color="info">
            A new submission will be allowed.
            <br />
            You'll be redirected to Submission Step.
          </Alert>
        )}

        {!confirmed && (
          <LoadingButton color="primary" onClick={handleConfirm} loading={loading} disabled={loading || isEmpty(paperResponse)}>
            Confirm Response
          </LoadingButton>
        )}
      </Fieldset>
    </>
  );
};
